import Slider from '../components/Shell/Slider';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { classNames, objectAccess } from '../components/helpers/functions';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useAsync from '../hooks/useAsync';
import { getAssets } from '../api';
import useModal from '../hooks/useModal';
import usePrevious from '../hooks/usePrevious';
import { TemplateField } from '../components/Attributes/Attributes_old';

const AssetsNearbySlider = () => {
	const { open: isNearbyAssetSliderOpen, setClose } = useModal('nearbyAssets');

	const {
		value: allAssets,
		status: isLoadingAllAssets,
		execute: onRefreshAssets,
	} = useAsync(getAssets, { immediate: false });

	const prevNearbyAssetsSliderOpen = usePrevious(isNearbyAssetSliderOpen);
	useEffect(() => {
		if (isNearbyAssetSliderOpen && !prevNearbyAssetsSliderOpen) {
			onRefreshAssets();
		}
	}, [isNearbyAssetSliderOpen]);

	const navigate = useNavigate();
	const locationDOM = useLocation();

	const [search, setSearch] = useState('');

	const params = locationDOM?.pathname?.split('/');
	const isAsset = params?.length && params[1] === 'assets';
	const id = params?.length && params?.length > 1 && params[2];

	const prevId = usePrevious(id);
	useEffect(() => {
		if (id !== prevId) {
			setClose();
		}
	}, [id]);

	if (!isAsset || !id) {
		return null;
	}

	const locationDirectory = {};

	const groupedAssetsByLocation = allAssets?.rows
		?.filter((asset) =>
			[
				'manufacturer.name',
				'model.name',
				'location.name',
				'category.name',
			].some((key) =>
				objectAccess(key, asset)?.toLowerCase().includes(search.toLowerCase()),
			),
		)
		.reduce((prev, cur) => {
			const locationName = cur?.location?.name;
			if (locationName) {
				if (prev[locationName]) {
					prev[locationName].push(cur);
				} else {
					prev[locationName] = [cur];
					locationDirectory[locationName] = cur?.location?.id;
				}
			} else {
				if (prev.Unassigned) {
					prev.Unassigned.push(cur);
				} else {
					prev['Unassigned'] = [cur];
				}
			}
			return prev;
		}, {});

	const sortedAssetsByLocation = Object.keys(groupedAssetsByLocation || {})
		.length
		? Object.fromEntries(
				Object.entries(groupedAssetsByLocation).sort(([a], [b]) =>
					a.localeCompare(b),
				),
			)
		: {};

	const activeAsset = allAssets?.rows?.find((o) => o.id === id);
	const activeLocation = Object.entries(locationDirectory).find(
		([name, idX]) => idX === activeAsset?.location_id,
	);
	const [activeLocationName, activeLocationId] = activeLocation || [null, null];

	return (
		<Slider name="nearbyAssets" title={'Nearby Assets'} fullWidth>
			<div className="px-6 pt-4">
				<TemplateField
					placeholder="Search..."
					onChange={(index, search) => setSearch(search)}
				/>
			</div>
			<div>
				{isLoadingAllAssets === 'pending' ? (
					<LoadingSkeleton />
				) : Object.keys(sortedAssetsByLocation).length ? (
					Object.keys(sortedAssetsByLocation)
						.sort((a, b) => {
							if (a === activeLocationName) return -1; // If a is the search value, place it before b.
							if (b === activeLocationName) return 1; // If b is the search value, place it after a.
							return 0; // Otherwise, leave them in the same order.
						})
						.map((key) => {
							const rowItems = sortedAssetsByLocation[key];
							return (
								<>
									<div
										className={classNames(
											'px-6 text-sm font-medium mt-3 bg-gray-100 py-3 text-gray-700 cursor-pointer',
										)}
										onClick={() =>
											key !== 'Unassigned'
												? navigate(`/assets/location/${locationDirectory[key]}`)
												: navigate(`/assets/location/unassigned`)
										}
									>
										{key}
									</div>
									<ul role="list" className="divide-y divide-gray-200">
										{rowItems
											?.sort((a, b) => {
												const aName = `${a?.manufacturer?.name} ${a?.model?.name}`;
												const bName = `${b?.manufacturer?.name} ${b?.model?.name}`;

												return aName.localeCompare(bName);
											})
											.map((item) => (
												<li
													key={item.id}
													className={classNames(
														'px-6 py-1.5 cursor-pointer text-sm',
														id === item?.id
															? 'bg-indigo-50 text-indigo-800 hover:text-indigo-900 font-medium'
															: 'white hover:bg-gray-50 text-gray-600 hover:text-gray-800',
													)}
													onClick={() => {
														navigate(`/assets/${item?.id}/overview`);
													}}
												>
													<div className="flex justify-between items-center">
														<div>
															<div>
																{item?.manufacturer?.name} {item?.model?.name}
															</div>
															<div className="text-gray-400 text-xs">
																{`S/N: ${item?.serialNumber}`}
															</div>
														</div>
														<div className="pl-2">
															<ChevronRightIcon className="w-4 h-4 text-gray-500" />
														</div>
													</div>
												</li>
											))}
									</ul>
								</>
							);
						})
				) : null}
			</div>
		</Slider>
	);
};

export default AssetsNearbySlider;
